import axios from 'axios';
import Cookies from 'js-cookie';
import { API_BASE_URL, endpoints } from '../utils/utils';
import { decrypt, encrypt } from '../shared/utils/crypto-utils';
import i18n from '../i18n';

export function createApiClient({ auth, loader, notify, setAuth }) {
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: { 'Accept': 'application/json' },
        withCredentials: true,
        withXSRFToken: true
    });

    const csrfCookieEndpoint = process.env.REACT_APP_BACKEND_URL + endpoints.csrf;

    const decryptResponse = (response) => {
        if (response?.data?.data && response?.data?.iv) {
            try {
                const decrypted = decrypt(response.data.data, response.data.iv);
                response.data = JSON.parse(decrypted);
            } catch (err) {
                console.error('Decryption failed', err);
            }
        }
        return response;
    };

    // Removed automatic translation transformation - now handled at component level

    // Request Interceptors
    api.interceptors.request.use(async (config) => {
        // CSRF Token Handling
        if (config.method !== 'get') {
            await axios.get(csrfCookieEndpoint, {
                withCredentials: true,
            });
        }
        return config;
    });

    api.interceptors.request.use((config) => {
        // Authorization Header
        if (!config.headers['Authorization']) {
            config.headers['Authorization'] = `Bearer ${auth?.accessToken}`;
        }
        return config;
    });

    api.interceptors.request.use((config) => {
        // Encryption (skip for FormData)
        if (config.data instanceof FormData) {
            return config;
        }

        if (!config.data?.data && !config.data?.iv &&
            ['post', 'put', 'patch'].includes(config.method)) {
            const { encrypted, iv } = encrypt(JSON.stringify(config.data));
            config.data = { data: encrypted, iv };
        }
        return config;
    });

    api.interceptors.request.use((config) => {
        // Loading Indicator
        if (!config.skipGlobalLoader) {
            loader.start();
        }
        return config;
    }, (error) => {
        if (!error?.config?.skipGlobalLoader) {
            loader.stop();
        }
        return Promise.reject(error);
    });

    // Add Accept-Language interceptor
    api.interceptors.request.use(config => {
        const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';  
        config.headers['Accept-Language'] = lang;
        return config;
    });

    // Response Interceptors
    api.interceptors.response.use(null, async (error) => {
        // Handle Access Token Refresh (403)
        const prevRequest = error?.config;
        if (error?.response?.status === 403 && !prevRequest?.sent) {
            prevRequest.sent = true;
            return api(prevRequest);
        }
        return Promise.reject(error);
    });

    api.interceptors.response.use(
        (response) => {
            // Only decrypt, don't transform translations - handled at component level
            return decryptResponse(response);
        },
        (error) => {
            // Decryption for error responses
            if (error.response) {
                error.response = decryptResponse(error.response);
            }
            return Promise.reject(error);
        }
    );

    api.interceptors.response.use(
        (response) => {
            // Success Handling
            if (!response.config?.skipGlobalLoader) {
                loader.stop();
            }
            if (!response.config?.skipSuccessNotification && response.data?.message) {
                notify.success(response.data.message);
            }
            return response;
        },
        (error) => {
            // Error Handling
            if (!error?.config?.skipGlobalLoader) {
                loader.stop();
            }

            if (error?.code === "ERR_NETWORK") {
                notify.error(error.message);
            } else if (error.response?.status === 422 && error.response?.data?.errors) {
                Object.values(error.response.data.errors).flat().forEach(msg => {
                    notify.error(msg);
                });
            }
            else if (error.response?.data?.message) {
                notify.error(error.response.data.message);
            }
            else {
                console.error('Unhandled API error:', error);
            }

            return Promise.reject(error);
        }
    );

    return api;
}
